// Migration script to convert existing base64 images to Firebase Storage
import { collection, getDocs, doc, updateDoc, writeBatch, query, where } from 'firebase/firestore';
import { db } from '../firebase/config';
import { uploadImageToStorage, sanitizeBrandName } from './imageUpload';

/**
 * Check if a string is a base64 image data URL
 */
const isBase64Image = (str) => {
  return str && typeof str === 'string' && str.startsWith('data:image/');
};

/**
 * Migrate a single brand's images from base64 to Firebase Storage
 */
const migrateBrandImages = async (brandId, brandData) => {
  const updates = {};
  let migrationCount = 0;

  const brandFolderName = sanitizeBrandName(brandData.name, brandId);
  console.log(`\n🔄 Migrating brand: ${brandData.name || brandId}`);
  console.log(`  📁 Storage folder: brands/${brandFolderName}/`);

  try {
    // Migrate logo image
    if (isBase64Image(brandData.logoImage)) {
      console.log(`  📷 Converting logo (${Math.round(brandData.logoImage.length / 1024)}KB base64)`);
      try {
        const logoUrl = await uploadImageToStorage(
          brandData.logoImage,
          `brands/${brandFolderName}/logo`,
          `logo_migrated_${Date.now()}.jpg`
        );
        updates.logoImage = logoUrl;
        migrationCount++;
        console.log(`  ✅ Logo migrated: ${logoUrl}`);
      } catch (error) {
        console.error(`  ❌ Logo migration failed:`, error.message);
      }
    } else if (brandData.logoImage) {
      console.log(`  ⏭️  Logo already migrated: ${brandData.logoImage}`);
    }

    // Migrate cover image
    if (isBase64Image(brandData.coverImage)) {
      console.log(`  📷 Converting cover (${Math.round(brandData.coverImage.length / 1024)}KB base64)`);
      try {
        const coverUrl = await uploadImageToStorage(
          brandData.coverImage,
          `brands/${brandFolderName}/cover`,
          `cover_migrated_${Date.now()}.jpg`
        );
        updates.coverImage = coverUrl;
        migrationCount++;
        console.log(`  ✅ Cover migrated: ${coverUrl}`);
      } catch (error) {
        console.error(`  ❌ Cover migration failed:`, error.message);
      }
    } else if (brandData.coverImage) {
      console.log(`  ⏭️  Cover already migrated: ${brandData.coverImage}`);
    }

    // Update brand document if we have changes
    if (Object.keys(updates).length > 0) {
      const brandRef = doc(db, 'brands', brandId);

      console.log(`  💾 Updating Firestore document with new URLs:`);
      Object.keys(updates).forEach(field => {
        console.log(`    ${field}: ${updates[field].substring(0, 100)}...`);
      });

      await updateDoc(brandRef, {
        ...updates,
        migratedAt: new Date(),
        updatedAt: new Date()
      });

      console.log(`  ✅ Brand document updated! Base64 strings replaced with ${Object.keys(updates).length} Firebase Storage URLs`);
    }

    return migrationCount;
  } catch (error) {
    console.error(`  ❌ Brand migration failed:`, error.message);
    return 0;
  }
};

/**
 * Migrate promotion images for a specific brand
 */
const migratePromotionImages = async (brandId, brandName = null) => {
  let migrationCount = 0;

  try {
    const brandFolderName = sanitizeBrandName(brandName, brandId);
    console.log(`\n🎯 Migrating promotions for brand: ${brandName || brandId}`);
    console.log(`  📁 Storage folder: brands/${brandFolderName}/promotions/`);

    // Get all promotions for this brand
    const promotionsRef = collection(db, 'brands', brandId, 'promotions');
    const promotionsSnapshot = await getDocs(promotionsRef);

    if (promotionsSnapshot.empty) {
      console.log(`  📭 No promotions found`);
      return 0;
    }

    console.log(`  📊 Found ${promotionsSnapshot.size} promotions`);

    // Process each promotion
    for (const promotionDoc of promotionsSnapshot.docs) {
      const promotionData = promotionDoc.data();
      const promotionId = promotionDoc.id;

      if (isBase64Image(promotionData.image)) {
        console.log(`    🔄 Converting promotion ${promotionId} image (${Math.round(promotionData.image.length / 1024)}KB base64)`);

        try {
          const promotionImageUrl = await uploadImageToStorage(
            promotionData.image,
            `brands/${brandFolderName}/promotions`,
            `promotion_${promotionId}_migrated_${Date.now()}.jpg`
          );

          // Update promotion document
          const promotionRef = doc(db, 'brands', brandId, 'promotions', promotionId);

          console.log(`    💾 Updating promotion document with Firebase Storage URL:`);
          console.log(`      image: ${promotionImageUrl.substring(0, 100)}...`);

          await updateDoc(promotionRef, {
            image: promotionImageUrl,
            migratedAt: new Date(),
            updatedAt: new Date()
          });

          migrationCount++;
          console.log(`    ✅ Promotion document updated! Base64 replaced with Firebase Storage URL`);
        } catch (error) {
          console.error(`    ❌ Promotion ${promotionId} migration failed:`, error.message);
        }
      } else if (promotionData.image) {
        console.log(`    ⏭️  Promotion ${promotionId} already migrated`);
      }
    }

    return migrationCount;
  } catch (error) {
    console.error(`  ❌ Promotions migration failed:`, error.message);
    return 0;
  }
};

/**
 * Run the complete migration for all brands
 */
export const runImageMigration = async (options = {}) => {
  const {
    dryRun = false,
    brandLimit = null,
    skipPromotions = false
  } = options;

  console.log(`\n🚀 Starting image migration...`);
  console.log(`   Mode: ${dryRun ? 'DRY RUN (no changes)' : 'LIVE MIGRATION'}`);
  console.log(`   Brand limit: ${brandLimit || 'No limit'}`);
  console.log(`   Skip promotions: ${skipPromotions}`);

  let totalBrandImages = 0;
  let totalPromotionImages = 0;
  let processedBrands = 0;
  let errors = 0;

  try {
    // Get all brands
    console.log(`\n📊 Fetching all brands...`);
    const brandsRef = collection(db, 'brands');
    const brandsSnapshot = await getDocs(brandsRef);

    console.log(`   Found ${brandsSnapshot.size} brands total`);

    const brandsToProcess = brandLimit
      ? brandsSnapshot.docs.slice(0, brandLimit)
      : brandsSnapshot.docs;

    console.log(`   Will process ${brandsToProcess.length} brands`);

    if (dryRun) {
      console.log(`\n🔍 DRY RUN - Analyzing what would be migrated:`);

      let base64BrandLogos = 0;
      let base64BrandCovers = 0;
      let base64PromotionCount = 0;
      const detailedBrands = [];

      for (const brandDoc of brandsToProcess) {
        const brandData = brandDoc.data();
        const brandId = brandDoc.id;

        let brandHasBase64 = false;
        const brandDetails = {
          id: brandId,
          name: brandData.name || brandId,
          hasBase64Logo: isBase64Image(brandData.logoImage),
          hasBase64Cover: isBase64Image(brandData.coverImage),
          promotionCount: 0
        };

        // Check brand images
        if (isBase64Image(brandData.logoImage)) {
          base64BrandLogos++;
          brandHasBase64 = true;
        }
        if (isBase64Image(brandData.coverImage)) {
          base64BrandCovers++;
          brandHasBase64 = true;
        }

        // Check promotion images
        if (!skipPromotions) {
          const promotionsRef = collection(db, 'brands', brandId, 'promotions');
          const promotionsSnapshot = await getDocs(promotionsRef);

          for (const promotionDoc of promotionsSnapshot.docs) {
            const promotionData = promotionDoc.data();
            if (isBase64Image(promotionData.image)) {
              base64PromotionCount++;
              brandDetails.promotionCount++;
              brandHasBase64 = true;
            }
          }
        }

        if (brandHasBase64) {
          detailedBrands.push(brandDetails);
          console.log(`   📸 Brand ${brandData.name || brandId}: ${isBase64Image(brandData.logoImage) ? 'Logo' : ''} ${isBase64Image(brandData.coverImage) ? 'Cover' : ''} ${brandDetails.promotionCount > 0 ? `+ ${brandDetails.promotionCount} promotions` : ''}`);
        }
      }

      const dryRunResults = {
        isDryRun: true,
        processedBrands: brandsToProcess.length,
        brandsWithBase64: detailedBrands.length,
        brandImages: base64BrandLogos + base64BrandCovers,
        promotionImages: base64PromotionCount,
        totalImages: base64BrandLogos + base64BrandCovers + base64PromotionCount,
        breakdown: {
          brandLogos: base64BrandLogos,
          brandCovers: base64BrandCovers,
          promotions: base64PromotionCount
        },
        detailedBrands,
        errors: 0
      };

      console.log(`\n📊 DRY RUN RESULTS:`);
      console.log(`   🏢 Brands processed: ${brandsToProcess.length}`);
      console.log(`   📸 Brands with base64 images: ${detailedBrands.length}`);
      console.log(`   🎯 Total images to migrate: ${dryRunResults.totalImages}`);
      console.log(`     - Brand logos: ${base64BrandLogos}`);
      console.log(`     - Brand covers: ${base64BrandCovers}`);
      console.log(`     - Promotions: ${base64PromotionCount}`);
      console.log(`\n🔄 Run with dryRun: false to perform actual migration`);

      return dryRunResults;
    }

    // Actual migration
    for (const brandDoc of brandsToProcess) {
      const brandData = brandDoc.data();
      const brandId = brandDoc.id;

      try {
        // Migrate brand images
        const brandImageCount = await migrateBrandImages(brandId, brandData);
        totalBrandImages += brandImageCount;

        // Migrate promotion images
        if (!skipPromotions) {
          const promotionImageCount = await migratePromotionImages(brandId, brandData.name);
          totalPromotionImages += promotionImageCount;
        }

        processedBrands++;

        // Add small delay to avoid rate limiting
        await new Promise(resolve => setTimeout(resolve, 500));

      } catch (error) {
        console.error(`❌ Error processing brand ${brandId}:`, error.message);
        errors++;
      }
    }

    // Final summary
    console.log(`\n🎉 MIGRATION COMPLETE!`);
    console.log(`   📊 Processed brands: ${processedBrands}`);
    console.log(`   🏢 Brand images migrated: ${totalBrandImages}`);
    console.log(`   🎯 Promotion images migrated: ${totalPromotionImages}`);
    console.log(`   📝 Total images migrated: ${totalBrandImages + totalPromotionImages}`);
    console.log(`   ❌ Errors: ${errors}`);

    return {
      processedBrands,
      brandImages: totalBrandImages,
      promotionImages: totalPromotionImages,
      totalImages: totalBrandImages + totalPromotionImages,
      errors
    };

  } catch (error) {
    console.error(`❌ Migration failed:`, error);
    throw error;
  }
};

/**
 * Quick check to see how many base64 images exist
 */
export const analyzeExistingImages = async () => {
  console.log(`\n🔍 Analyzing existing images...`);

  try {
    const brandsRef = collection(db, 'brands');
    const brandsSnapshot = await getDocs(brandsRef);

    let base64BrandLogos = 0;
    let base64BrandCovers = 0;
    let base64Promotions = 0;
    let migratedBrandLogos = 0;
    let migratedBrandCovers = 0;
    let migratedPromotions = 0;

    for (const brandDoc of brandsSnapshot.docs) {
      const brandData = brandDoc.data();
      const brandId = brandDoc.id;

      // Check brand images
      if (isBase64Image(brandData.logoImage)) {
        base64BrandLogos++;
      } else if (brandData.logoImage) {
        migratedBrandLogos++;
      }

      if (isBase64Image(brandData.coverImage)) {
        base64BrandCovers++;
      } else if (brandData.coverImage) {
        migratedBrandCovers++;
      }

      // Check promotions
      const promotionsRef = collection(db, 'brands', brandId, 'promotions');
      const promotionsSnapshot = await getDocs(promotionsRef);

      for (const promotionDoc of promotionsSnapshot.docs) {
        const promotionData = promotionDoc.data();
        if (isBase64Image(promotionData.image)) {
          base64Promotions++;
        } else if (promotionData.image) {
          migratedPromotions++;
        }
      }
    }

    console.log(`\n📊 ANALYSIS RESULTS:`);
    console.log(`   🏢 Total brands: ${brandsSnapshot.size}`);
    console.log(`\n   📸 Brand Logos:`);
    console.log(`     🔄 Base64: ${base64BrandLogos}`);
    console.log(`     ✅ Migrated: ${migratedBrandLogos}`);
    console.log(`\n   📸 Brand Covers:`);
    console.log(`     🔄 Base64: ${base64BrandCovers}`);
    console.log(`     ✅ Migrated: ${migratedBrandCovers}`);
    console.log(`\n   🎯 Promotion Images:`);
    console.log(`     🔄 Base64: ${base64Promotions}`);
    console.log(`     ✅ Migrated: ${migratedPromotions}`);
    console.log(`\n   📝 TOTAL TO MIGRATE: ${base64BrandLogos + base64BrandCovers + base64Promotions}`);

    return {
      brands: brandsSnapshot.size,
      toMigrate: {
        brandLogos: base64BrandLogos,
        brandCovers: base64BrandCovers,
        promotions: base64Promotions,
        total: base64BrandLogos + base64BrandCovers + base64Promotions
      },
      migrated: {
        brandLogos: migratedBrandLogos,
        brandCovers: migratedBrandCovers,
        promotions: migratedPromotions,
        total: migratedBrandLogos + migratedBrandCovers + migratedPromotions
      }
    };

  } catch (error) {
    console.error(`❌ Analysis failed:`, error);
    throw error;
  }
};